// Derives every ratio, roll-up and chart series the Finance Analyst dashboard
// shows, from the as-printed statement lines in constants/sattvaFinancials.js.
//
// Two conventions worth knowing before reading the numbers:
//
//  * Balance-sheet-driven ratios (returns, turnover, days) use the AVERAGE of
//    opening and closing balances. FY22 is the first year in the series, so it
//    has no opening balance and falls back to the closing figure — its returns
//    are therefore not strictly comparable with FY23-FY25 and are flagged in
//    the UI.
//  * Margins are struck on TOTAL INCOME, not revenue from operations. Other
//    income is 23% of total income by FY25 here, so a revenue-only denominator
//    would overstate every margin.

import {
  FISCAL_YEARS,
  YEAR_META,
  BALANCE_SHEET,
  PROFIT_AND_LOSS,
} from '../constants/sattvaFinancials';

// ---------- small numeric helpers ----------

const N = FISCAL_YEARS.length;

const map = (fn) => FISCAL_YEARS.map((_, i) => fn(i));

/** Element-wise sum of any number of series. */
const add = (...series) => map((i) => series.reduce((t, s) => t + (s[i] || 0), 0));

/** Element-wise a - b. */
const sub = (a, b) => map((i) => a[i] - b[i]);

/** Safe division; null (rendered as an em dash) when the denominator vanishes. */
const div = (a, b) => map((i) => (b[i] ? a[i] / b[i] : null));

const pct = (a, b) => map((i) => (b[i] ? (a[i] / b[i]) * 100 : null));

/** Average of opening and closing balance; falls back to closing in year one. */
const avgBalance = (series) => map((i) => (i === 0 ? series[i] : (series[i] + series[i - 1]) / 2));

/** Year-on-year growth %, null for the first year. */
const yoy = (series) =>
  map((i) => {
    if (i === 0) return null;
    const prev = series[i - 1];
    // A sign flip makes a percentage meaningless rather than merely large.
    if (!prev || (prev < 0) !== (series[i] < 0)) return null;
    return ((series[i] - prev) / Math.abs(prev)) * 100;
  });

/** Compound annual growth across the whole series. */
const cagr = (series) => {
  const first = series[0];
  const last = series[N - 1];
  if (!first || first <= 0 || last <= 0) return null;
  return (Math.pow(last / first, 1 / (N - 1)) - 1) * 100;
};

const days = (balance, flow) => map((i) => (flow[i] ? (balance[i] / flow[i]) * 365 : null));

// ---------- the derivation ----------

export function buildFinanceAnalytics() {
  const bs = BALANCE_SHEET;
  const pl = PROFIT_AND_LOSS;

  // --- P&L roll-ups ---
  // EBIT/EBITDA are built up from PBT because the statements report neither.
  const ebit = add(pl.profitBeforeTax, pl.financeCosts);
  const ebitda = add(ebit, pl.depreciationAndAmortization);

  // Cost of sales for a developer: land/project spend, traded stock and stores,
  // adjusted for the inventory movement that capitalises unsold work.
  const costOfSales = add(
    pl.projectDevelopmentExpenses,
    pl.purchaseOfStockInTrade,
    pl.costOfStoresMaterialSold,
    pl.changesInInventories
  );
  const grossProfit = sub(pl.revenueFromOperations, costOfSales);

  const totalTax = add(
    pl.currentTax,
    pl.taxForEarlierYears,
    pl.excessShortProvisionEarlier,
    pl.deferredTax
  );

  // Operating profit excluding other income — how much of the result the
  // development business itself carries.
  const coreOperatingProfit = sub(ebitda, pl.otherIncome);

  // --- balance-sheet roll-ups ---
  const totalBorrowings = add(bs.nonCurrentBorrowings, bs.currentBorrowings);
  const leaseLiabilities = add(bs.nonCurrentLeaseLiabilities, bs.currentLeaseLiabilities);
  const grossDebt = add(totalBorrowings, leaseLiabilities);
  const liquidAssets = add(bs.cashAndCashEquivalents, bs.otherBankBalances, bs.currentInvestments);
  const netDebt = sub(totalBorrowings, liquidAssets);
  const ownersEquity = add(bs.equityShareCapital, bs.otherEquity);
  const capitalEmployed = add(bs.totalEquity, totalBorrowings);
  const workingCapital = sub(bs.totalCurrentAssets, bs.totalCurrentLiabilities);
  const tradePayables = add(bs.tradePayablesMsme, bs.tradePayablesOthers);
  const totalLiabilities = add(bs.totalNonCurrentLiabilities, bs.totalCurrentLiabilities);
  const quickAssets = sub(bs.totalCurrentAssets, bs.inventories);

  // The three lines that actually represent the development portfolio:
  // completed rent-yielding property, property under construction, and unsold
  // stock. This is the asset base the whole group is really a bet on.
  const realEstateAssetBase = add(
    bs.investmentProperty,
    bs.investmentPropertyUnderDev,
    bs.inventories
  );

  // Averages for anything that mixes a flow with a stock.
  const avgEquity = avgBalance(bs.totalEquity);
  const avgAssets = avgBalance(bs.totalAssets);
  const avgCapitalEmployed = avgBalance(capitalEmployed);
  const avgReceivables = avgBalance(bs.tradeReceivables);
  const avgInventories = avgBalance(bs.inventories);
  const avgPayables = avgBalance(tradePayables);

  const receivableDays = days(avgReceivables, pl.revenueFromOperations);
  const inventoryDays = days(avgInventories, costOfSales);
  const payableDays = days(avgPayables, costOfSales);
  const cashConversionCycle = map((i) => {
    const parts = [receivableDays[i], inventoryDays[i], payableDays[i]];
    if (parts.some((p) => p === null)) return null;
    return parts[0] + parts[1] - parts[2];
  });

  const derived = {
    // scale
    ebit,
    ebitda,
    costOfSales,
    grossProfit,
    coreOperatingProfit,
    totalTax,
    totalBorrowings,
    leaseLiabilities,
    grossDebt,
    liquidAssets,
    netDebt,
    ownersEquity,
    capitalEmployed,
    workingCapital,
    tradePayables,
    totalLiabilities,
    quickAssets,
    realEstateAssetBase,

    // margins (on total income unless the name says otherwise)
    grossMargin: pct(grossProfit, pl.revenueFromOperations),
    ebitdaMargin: pct(ebitda, pl.totalIncome),
    ebitMargin: pct(ebit, pl.totalIncome),
    pbtMargin: pct(pl.profitBeforeTax, pl.totalIncome),
    patMargin: pct(pl.profitForTheYear, pl.totalIncome),
    patOnRevenue: pct(pl.profitForTheYear, pl.revenueFromOperations),
    otherIncomeShare: pct(pl.otherIncome, pl.totalIncome),
    financeCostRatio: pct(pl.financeCosts, pl.totalIncome),
    employeeCostRatio: pct(pl.employeeBenefitsExpenses, pl.totalIncome),
    otherExpenseRatio: pct(pl.otherExpenses, pl.totalIncome),
    effectiveTaxRate: pct(totalTax, pl.profitBeforeTax),
    jvContribution: pct(pl.shareOfProfitFromJv, pl.profitForTheYear),

    // returns
    roe: pct(pl.profitForTheYear, avgEquity),
    roeOwners: pct(pl.tciOwnersOfParent, avgBalance(ownersEquity)),
    roa: pct(pl.profitForTheYear, avgAssets),
    roce: pct(ebit, avgCapitalEmployed),

    // turnover & cycle
    assetTurnover: div(pl.revenueFromOperations, avgAssets),
    capitalEmployedTurnover: div(pl.revenueFromOperations, avgCapitalEmployed),
    inventoryTurnover: div(costOfSales, avgInventories),
    receivableDays,
    inventoryDays,
    payableDays,
    cashConversionCycle,

    // liquidity
    currentRatio: div(bs.totalCurrentAssets, bs.totalCurrentLiabilities),
    quickRatio: div(quickAssets, bs.totalCurrentLiabilities),
    cashRatio: div(liquidAssets, bs.totalCurrentLiabilities),
    workingCapitalToAssets: pct(workingCapital, bs.totalAssets),

    // leverage & solvency
    debtToEquity: div(totalBorrowings, bs.totalEquity),
    netDebtToEquity: div(netDebt, bs.totalEquity),
    debtToTotalCapital: pct(totalBorrowings, capitalEmployed),
    debtToAssets: pct(totalBorrowings, bs.totalAssets),
    equityRatio: pct(bs.totalEquity, bs.totalAssets),
    liabilitiesToEquity: div(totalLiabilities, bs.totalEquity),
    netDebtToEbitda: div(netDebt, ebitda),
    interestCoverage: div(ebit, pl.financeCosts),
    ebitdaInterestCoverage: div(ebitda, pl.financeCosts),

    // asset mix
    nonCurrentAssetShare: pct(bs.totalNonCurrentAssets, bs.totalAssets),
    investmentShare: pct(bs.nonCurrentInvestments, bs.totalAssets),
    inventoryShare: pct(bs.inventories, bs.totalAssets),
    realEstateShare: pct(realEstateAssetBase, bs.totalAssets),
    goodwillToEquity: pct(bs.goodwill, bs.totalEquity),

    // growth
    revenueGrowth: yoy(pl.revenueFromOperations),
    totalIncomeGrowth: yoy(pl.totalIncome),
    ebitdaGrowth: yoy(ebitda),
    pbtGrowth: yoy(pl.profitBeforeTax),
    patGrowth: yoy(pl.profitForTheYear),
    assetGrowth: yoy(bs.totalAssets),
    equityGrowth: yoy(bs.totalEquity),
    borrowingsGrowth: yoy(totalBorrowings),
    inventoryGrowth: yoy(bs.inventories),
    receivableGrowth: yoy(bs.tradeReceivables),
    epsGrowth: yoy(pl.earningsPerShare),
  };

  const cagrs = {
    revenue: cagr(pl.revenueFromOperations),
    totalIncome: cagr(pl.totalIncome),
    ebitda: cagr(ebitda),
    pat: cagr(pl.profitForTheYear),
    totalAssets: cagr(bs.totalAssets),
    equity: cagr(bs.totalEquity),
    realEstateAssetBase: cagr(realEstateAssetBase),
    eps: cagr(pl.earningsPerShare),
  };

  // ---------- chart-ready series ----------

  const row = (i) => ({ year: FISCAL_YEARS[i], audited: YEAR_META[FISCAL_YEARS[i]].audited });

  const series = {
    performance: map((i) => ({
      ...row(i),
      revenue: pl.revenueFromOperations[i],
      otherIncome: pl.otherIncome[i],
      totalIncome: pl.totalIncome[i],
      ebitda: ebitda[i],
      pbt: pl.profitBeforeTax[i],
      pat: pl.profitForTheYear[i],
      patMargin: derived.patMargin[i],
      ebitdaMargin: derived.ebitdaMargin[i],
    })),

    margins: map((i) => ({
      ...row(i),
      ebitdaMargin: derived.ebitdaMargin[i],
      ebitMargin: derived.ebitMargin[i],
      pbtMargin: derived.pbtMargin[i],
      patMargin: derived.patMargin[i],
      effectiveTaxRate: derived.effectiveTaxRate[i],
    })),

    expenseMix: map((i) => ({
      ...row(i),
      'Project development': pl.projectDevelopmentExpenses[i],
      'Stock & stores': pl.purchaseOfStockInTrade[i] + pl.costOfStoresMaterialSold[i],
      'Inventory movement': pl.changesInInventories[i],
      Employee: pl.employeeBenefitsExpenses[i],
      Finance: pl.financeCosts[i],
      'Depreciation': pl.depreciationAndAmortization[i],
      Other: pl.otherExpenses[i],
    })),

    // Where profit actually comes from: the consolidated entities' own result
    // versus the equity-accounted JV share.
    profitBridge: map((i) => ({
      ...row(i),
      'Own operations (post-tax)': pl.profitBeforeShareOfJv[i],
      'Associates & JVs': pl.shareOfProfitFromJv[i],
      pat: pl.profitForTheYear[i],
      jvContribution: derived.jvContribution[i],
    })),

    incomeSplit: map((i) => ({
      ...row(i),
      'Revenue from operations': pl.revenueFromOperations[i],
      'Other income': pl.otherIncome[i],
      otherIncomeShare: derived.otherIncomeShare[i],
    })),

    assetMix: map((i) => ({
      ...row(i),
      'Investment property': bs.investmentProperty[i],
      'Property under development': bs.investmentPropertyUnderDev[i],
      Inventories: bs.inventories[i],
      Investments: bs.nonCurrentInvestments[i] + bs.currentInvestments[i],
      Receivables: bs.tradeReceivables[i],
      'Cash & bank': bs.cashAndCashEquivalents[i] + bs.otherBankBalances[i],
      'Loans given': bs.loans[i],
      'Other assets':
        bs.totalAssets[i] -
        (bs.investmentProperty[i] +
          bs.investmentPropertyUnderDev[i] +
          bs.inventories[i] +
          bs.nonCurrentInvestments[i] +
          bs.currentInvestments[i] +
          bs.tradeReceivables[i] +
          bs.cashAndCashEquivalents[i] +
          bs.otherBankBalances[i] +
          bs.loans[i]),
    })),

    fundingMix: map((i) => ({
      ...row(i),
      Equity: bs.totalEquity[i],
      Borrowings: totalBorrowings[i],
      'Trade payables': tradePayables[i],
      'Other liabilities':
        totalLiabilities[i] - totalBorrowings[i] - tradePayables[i],
    })),

    leverage: map((i) => ({
      ...row(i),
      totalBorrowings: totalBorrowings[i],
      netDebt: netDebt[i],
      equity: bs.totalEquity[i],
      debtToEquity: derived.debtToEquity[i],
      netDebtToEquity: derived.netDebtToEquity[i],
      interestCoverage: derived.interestCoverage[i],
      netDebtToEbitda: derived.netDebtToEbitda[i],
    })),

    liquidity: map((i) => ({
      ...row(i),
      currentRatio: derived.currentRatio[i],
      quickRatio: derived.quickRatio[i],
      cashRatio: derived.cashRatio[i],
      workingCapital: workingCapital[i],
    })),

    returns: map((i) => ({
      ...row(i),
      roe: derived.roe[i],
      roa: derived.roa[i],
      roce: derived.roce[i],
    })),

    workingCapitalCycle: map((i) => ({
      ...row(i),
      receivableDays: derived.receivableDays[i],
      inventoryDays: derived.inventoryDays[i],
      payableDays: derived.payableDays[i],
      cashConversionCycle: derived.cashConversionCycle[i],
    })),

    balanceSheetGrowth: map((i) => ({
      ...row(i),
      totalAssets: bs.totalAssets[i],
      equity: bs.totalEquity[i],
      borrowings: totalBorrowings[i],
      realEstateAssetBase: realEstateAssetBase[i],
    })),

    perShare: map((i) => ({
      ...row(i),
      eps: pl.earningsPerShare[i],
      epsGrowth: derived.epsGrowth[i],
    })),
  };

  const latest = N - 1;
  const prior = N - 2;

  return {
    years: FISCAL_YEARS,
    meta: YEAR_META,
    bs,
    pl,
    derived,
    cagrs,
    series,
    latest,
    prior,
    /** Value of a raw or derived line in the newest year. */
    at: (line, i = latest) => (Array.isArray(line) ? line[i] : null),
  };
}

/**
 * Ratio table definition — one row per metric, shared by the Ratios tab and
 * anything else that wants the full grid. `format` is resolved in the
 * component so number formatting stays in one place.
 */
export const RATIO_GROUPS = [
  {
    group: 'Growth',
    rows: [
      { key: 'revenueGrowth', label: 'Revenue growth', format: 'pct', good: 'up' },
      { key: 'totalIncomeGrowth', label: 'Total income growth', format: 'pct', good: 'up' },
      { key: 'ebitdaGrowth', label: 'EBITDA growth', format: 'pct', good: 'up' },
      { key: 'pbtGrowth', label: 'PBT growth', format: 'pct', good: 'up' },
      { key: 'patGrowth', label: 'PAT growth', format: 'pct', good: 'up' },
      { key: 'assetGrowth', label: 'Total asset growth', format: 'pct', good: 'up' },
      { key: 'equityGrowth', label: 'Net worth growth', format: 'pct', good: 'up' },
      { key: 'borrowingsGrowth', label: 'Borrowings growth', format: 'pct', good: 'down' },
    ],
  },
  {
    group: 'Profitability',
    rows: [
      { key: 'grossMargin', label: 'Gross margin (on revenue)', format: 'pct', good: 'up' },
      { key: 'ebitdaMargin', label: 'EBITDA margin', format: 'pct', good: 'up' },
      { key: 'ebitMargin', label: 'EBIT margin', format: 'pct', good: 'up' },
      { key: 'pbtMargin', label: 'PBT margin', format: 'pct', good: 'up' },
      { key: 'patMargin', label: 'PAT margin', format: 'pct', good: 'up' },
      { key: 'patOnRevenue', label: 'PAT / revenue from operations', format: 'pct', good: 'up' },
      { key: 'effectiveTaxRate', label: 'Effective tax rate', format: 'pct', good: 'down' },
      { key: 'jvContribution', label: 'JV & associate share of PAT', format: 'pct', good: 'flat' },
      { key: 'otherIncomeShare', label: 'Other income share of total income', format: 'pct', good: 'flat' },
    ],
  },
  {
    group: 'Returns',
    rows: [
      { key: 'roe', label: 'Return on equity', format: 'pct', good: 'up' },
      { key: 'roeOwners', label: 'Return on owners equity', format: 'pct', good: 'up' },
      { key: 'roa', label: 'Return on assets', format: 'pct', good: 'up' },
      { key: 'roce', label: 'Return on capital employed', format: 'pct', good: 'up' },
    ],
  },
  {
    group: 'Leverage & solvency',
    rows: [
      { key: 'debtToEquity', label: 'Debt / equity', format: 'x', good: 'down' },
      { key: 'netDebtToEquity', label: 'Net debt / equity', format: 'x', good: 'down' },
      { key: 'debtToTotalCapital', label: 'Debt / total capital', format: 'pct', good: 'down' },
      { key: 'debtToAssets', label: 'Debt / total assets', format: 'pct', good: 'down' },
      { key: 'equityRatio', label: 'Equity / total assets', format: 'pct', good: 'up' },
      { key: 'liabilitiesToEquity', label: 'Total liabilities / equity', format: 'x', good: 'down' },
      { key: 'netDebtToEbitda', label: 'Net debt / EBITDA', format: 'x', good: 'down' },
      { key: 'interestCoverage', label: 'Interest coverage (EBIT)', format: 'x', good: 'up' },
      { key: 'ebitdaInterestCoverage', label: 'Interest coverage (EBITDA)', format: 'x', good: 'up' },
    ],
  },
  {
    group: 'Liquidity',
    rows: [
      { key: 'currentRatio', label: 'Current ratio', format: 'x', good: 'up' },
      { key: 'quickRatio', label: 'Quick ratio', format: 'x', good: 'up' },
      { key: 'cashRatio', label: 'Cash ratio', format: 'x', good: 'up' },
      { key: 'workingCapitalToAssets', label: 'Working capital / assets', format: 'pct', good: 'up' },
    ],
  },
  {
    group: 'Efficiency',
    rows: [
      { key: 'assetTurnover', label: 'Asset turnover', format: 'x', good: 'up' },
      { key: 'capitalEmployedTurnover', label: 'Capital employed turnover', format: 'x', good: 'up' },
      { key: 'inventoryTurnover', label: 'Inventory turnover', format: 'x', good: 'up' },
      { key: 'receivableDays', label: 'Receivable days', format: 'days', good: 'down' },
      { key: 'inventoryDays', label: 'Inventory days', format: 'days', good: 'down' },
      { key: 'payableDays', label: 'Payable days', format: 'days', good: 'up' },
      { key: 'cashConversionCycle', label: 'Cash conversion cycle', format: 'days', good: 'down' },
    ],
  },
  {
    group: 'Asset mix',
    rows: [
      { key: 'nonCurrentAssetShare', label: 'Non-current assets / total assets', format: 'pct', good: 'flat' },
      { key: 'realEstateShare', label: 'Real estate asset base / total assets', format: 'pct', good: 'flat' },
      { key: 'investmentShare', label: 'Non-current investments / total assets', format: 'pct', good: 'flat' },
      { key: 'inventoryShare', label: 'Inventories / total assets', format: 'pct', good: 'flat' },
      { key: 'goodwillToEquity', label: 'Goodwill / net worth', format: 'pct', good: 'down' },
    ],
  },
];

export default buildFinanceAnalytics;
